import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Building2, Users, Zap, BarChart3, ArrowRight, CheckCircle, Rocket, Target, Lightbulb, DollarSign } from 'lucide-react';

const SmallBusinessUseCase = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white py-20 lg:py-32">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#c4ffa0]/20 text-[#2d4a1e] text-sm font-medium mb-8">
              <Building2 className="w-4 h-4 mr-2" />
              Small Business Solutions
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Boost Sales with
              <span className="text-[#2d4a1e]"> Instant Ads</span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Small businesses spend 30-50% of their marketing budget on repetitive creative tasks. 
              Our AI platform turns your brand into instantly scalable campaigns that drive results.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-[#c4ffa0] to-green-400 text-[#2d4a1e] px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                Start Free Trial
              </button>
              <button className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-xl font-semibold text-lg hover:border-[#c4ffa0] hover:text-[#2d4a1e] transition-all duration-300">
                Watch Demo
              </button>
            </div>
          </div>
        </div>
        
        {/* Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-32 h-32 bg-[#c4ffa0]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-40 h-40 bg-green-400/10 rounded-full blur-3xl"></div>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#c4ffa0]/20 text-[#2d4a1e] text-sm font-medium mb-4">
              <Lightbulb className="w-4 h-4 mr-2" />
              Core Value
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              AI-Powered Creative Automation for Small Business
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform your brand guidelines into instantly scalable campaigns that drive sales and growth
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300 hover:border-[#c4ffa0]/30">
              <div className="w-14 h-14 bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-xl flex items-center justify-center mb-6">
                <Rocket className="w-7 h-7 text-[#2d4a1e]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Instant Campaign Creation</h3>
              <p className="text-gray-600 mb-4">
                Upload your brand guide once and generate unlimited ads, social posts, and marketing materials instantly.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300 hover:border-[#c4ffa0]/30">
              <div className="w-14 h-14 bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-xl flex items-center justify-center mb-6">
                <DollarSign className="w-7 h-7 text-[#2d4a1e]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Reduce Creative Costs</h3>
              <p className="text-gray-600 mb-4">
                Cut your creative production costs by 30-50% while maintaining professional quality and brand consistency.
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300 hover:border-[#c4ffa0]/30">
              <div className="w-14 h-14 bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-xl flex items-center justify-center mb-6">
                <BarChart3 className="w-7 h-7 text-[#2d4a1e]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Scale Your Marketing</h3>
              <p className="text-gray-600 mb-4">
                Launch more campaigns, test more ideas, and reach more customers without increasing your creative budget.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Examples Section */}
      <section className="py-20 bg-gradient-to-r from-[#c4ffa0]/5 to-green-400/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Perfect For Small Businesses Like Yours
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              See how different types of small businesses use our platform to grow
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Example 1 */}
            <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-400 to-blue-500 rounded-xl flex items-center justify-center mb-6">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Coffee Shops & Cafés</h3>
              <p className="text-gray-600 mb-6">
                Create seasonal drink promotions, food photography, and local event marketing that drives foot traffic.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Seasonal drink campaigns
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Food photography ads
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Local event promotions
                </li>
              </ul>
            </div>

            {/* Example 2 */}
            <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-400 to-purple-500 rounded-xl flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Plant Stores & Nurseries</h3>
              <p className="text-gray-600 mb-6">
                Generate plant care content, seasonal promotions, and educational materials that grow your customer base.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Plant care guides
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Seasonal plant promotions
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Educational content
                </li>
              </ul>
            </div>

            {/* Example 3 */}
            <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-green-400 to-green-500 rounded-xl flex items-center justify-center mb-6">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Local Retail Stores</h3>
              <p className="text-gray-600 mb-6">
                Create seasonal campaigns, product promotions, and local event marketing materials in minutes, not days.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Seasonal campaign templates
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Product promotion ads
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Local event marketing
                </li>
              </ul>
            </div>

            {/* Example 4 */}
            <div className="bg-white p-8 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-r from-orange-400 to-orange-500 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">E-commerce Startups</h3>
              <p className="text-gray-600 mb-6">
                Create product ads, social media content, and email marketing graphics that convert browsers into buyers.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Product showcase ads
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Social media content
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 text-[#c4ffa0] mr-3" />
                  Email marketing graphics
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#c4ffa0]/10 to-green-400/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Ready to Boost Your Small Business Marketing?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join thousands of small businesses that trust kinOS to create professional marketing materials in minutes, not days.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-gradient-to-r from-[#c4ffa0] to-green-400 text-[#2d4a1e] px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transition-all duration-300 hover:scale-105 flex items-center justify-center">
              Start Free Trial
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
            <button className="border-2 border-[#2d4a1e] text-[#2d4a1e] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#2d4a1e] hover:text-white transition-all duration-300">
              Schedule Demo
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SmallBusinessUseCase; 